/**
 * Login screen — pre-auth surface. Single-column centered form,
 * minimal chrome (no AppShell). Offers a "dev login" shortcut when
 * the backend is not in production so engineers can get a token
 * without provisioning a real account.
 */

import { useEffect, useState, type FormEvent } from "react";
import { Cog, KeyRound, Shield, Sparkles } from "lucide-react";
import { Badge, Button, Card, CardHeader, Input } from "@vseh/ui";

import { useAuth } from "../shared/auth/AuthProvider";
import { authApi, type OIDCConfigDto } from "../shared/api";
import { useT } from "../shared/i18n";


/**
 * Dev-only personas surfaced as click-to-sign-in tiles. Each
 * role maps 1:1 to `auth_core.scopes.ROLE_PRESETS`. Avatars
 * are pulled from i.pravatar.cc with a stable seed so the
 * same photo renders across reloads / browsers / operators.
 * The component falls back to a neutral SVG circle if the
 * image can't load (offline dev / blocked image hosts).
 *
 * `scopeCount` is hand-counted from ROLE_PRESETS so the tile
 * can hint at the permission breadth without an extra round-
 * trip; the actual scopes still come from the dev-token
 * endpoint at sign-in time.
 */
interface DevPersona {
  role:        "admin" | "dispatcher" | "operator" | "viewer";
  nameKey:     string;
  labelKey:    string;
  avatarSeed:  string;
  scopeCount:  number;
  /** Реальные креды демо-аккаунта. Используются на проде, когда
   *  /auth/dev-token отключён (VSEH_ENV=production). Если креды
   *  пустые — клик уходит в legacy devLogin (dev-режим). */
  demoEmail?:    string;
  demoPassword?: string;
  demoTenant?:   string;
}

const DEV_PERSONAS: DevPersona[] = [
  { role: "admin",      nameKey: "login.dev.role.admin.name",      labelKey: "login.dev.role.admin.label",      avatarSeed: "vseh-admin",      scopeCount: 21,
    demoEmail: "igor@vsteh.demo",  demoPassword: "igor1234",  demoTenant: "vsteh" },
  { role: "dispatcher", nameKey: "login.dev.role.dispatcher.name", labelKey: "login.dev.role.dispatcher.label", avatarSeed: "vseh-dispatcher", scopeCount: 15,
    demoEmail: "anna@vsteh.demo",  demoPassword: "anna1234",  demoTenant: "vsteh" },
  { role: "operator",   nameKey: "login.dev.role.operator.name",   labelKey: "login.dev.role.operator.label",   avatarSeed: "vseh-operator",   scopeCount:  8,
    demoEmail: "maria@vsteh.demo", demoPassword: "maria1234", demoTenant: "vsteh" },
  { role: "viewer",     nameKey: "login.dev.role.viewer.name",     labelKey: "login.dev.role.viewer.label",     avatarSeed: "vseh-viewer",     scopeCount:  8,
    demoEmail: "denis@vsteh.demo", demoPassword: "denis1234", demoTenant: "vsteh" },
];


export function LoginPage(): JSX.Element {
  const t = useT();
  const { login, devLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [oidc, setOidc] = useState<OIDCConfigDto | null>(null);

  // OIDC discovery — runs once on mount. We tolerate the call failing
  // (e.g. backend offline) by leaving the SSO button hidden rather than
  // surfacing a noisy error — the password form still works.
  useEffect(() => {
    let alive = true;
    authApi.oidcConfig()
      .then((c) => { if (alive) setOidc(c); })
      .catch(() => { /* SSO simply hidden */ });
    return () => { alive = false; };
  }, []);

  const onSsoLogin = () => {
    if (!oidc?.enabled || !oidc.authorize_url || !oidc.client_id) return;
    // PKCE-light: we route through the IdP's authorize endpoint with
    // a state we can verify on return. Full PKCE handshake lives in
    // the callback handler — out of scope for the redirect button.
    const state = crypto.randomUUID();
    sessionStorage.setItem("vseh_oidc_state", state);
    const redirectUri = `${window.location.origin}/auth/oidc/callback`;
    const params = new URLSearchParams({
      response_type: "code",
      client_id:     oidc.client_id,
      redirect_uri:  redirectUri,
      scope:         "openid profile email",
      state,
    });
    window.location.href = `${oidc.authorize_url}?${params.toString()}`;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await login(email, password, tenantSlug || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDevLogin = async (role: string = "admin") => {
    setSubmitting(true);
    setError(null);
    try {
      // На проде /auth/dev-token возвращает 404 (VSEH_ENV=production),
      // поэтому плитки должны логиниться через обычный /auth/login
      // реальными демо-кредами. Если у персоны прописан demoEmail —
      // идём этим путём; иначе откатываемся к legacy dev-token endpoint
      // (для локальной разработки).
      const persona = DEV_PERSONAS.find((p) => p.role === role);
      if (persona?.demoEmail && persona.demoPassword) {
        await login(persona.demoEmail, persona.demoPassword, persona.demoTenant);
      } else {
        await devLogin(role);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-base-50 p-6">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-2 text-base-700">
          <span className="grid size-7 place-items-center rounded-md bg-primary-900 text-white">
            <Cog className="size-4" />
          </span>
          <span className="font-display text-xl font-semibold">VSEH AI OS</span>
        </div>

        <Card>
          <CardHeader
            title={t("login.title")}
            description={t("login.subtitle")}
          />

          <form className="space-y-3" onSubmit={onSubmit}>
            <Input
              label={t("login.field.email")}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
            <Input
              label={t("login.field.password")}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
            <Input
              label={t("login.field.tenant")}
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              placeholder={t("login.field.tenant.placeholder")}
              hint={t("login.field.tenant.hint")}
            />

            {error && (
              <div className="rounded-sm border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="ai"
              leftIcon={<KeyRound className="size-4" />}
              loading={submitting}
              disabled={!email || !password}
              className="w-full"
            >
              {t("login.submit")}
            </Button>
          </form>

          {oidc?.enabled && (
            <div className="mt-4 border-t border-base-200 pt-4">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                leftIcon={<Shield className="size-4" />}
                onClick={onSsoLogin}
                disabled={submitting}
                className="w-full"
              >
                {t("login.sso")}
              </Button>
              <p className="mt-1.5 text-2xs text-base-500">
                {t("login.sso.via")} {new URL(oidc.authorize_url ?? "https://example").host}
              </p>
            </div>
          )}

          <div className="mt-6 border-t border-base-200 pt-4">
            <div className="mb-2 flex items-center gap-2">
              <Badge tone="neutral">{t("login.dev.badge")}</Badge>
              <span className="text-xs text-base-500">
                {t("login.dev.quick.hint")}
              </span>
            </div>

            <div className="mb-2 text-2xs font-medium uppercase tracking-wide text-base-500">
              {t("login.dev.quick.title")}
            </div>

            {/* 2x2 grid of persona tiles. Each is a button so
                keyboard navigation works; the click area is the
                whole card. */}
            <div className="grid grid-cols-2 gap-2">
              {DEV_PERSONAS.map((p) => (
                <button
                  key={p.role}
                  type="button"
                  disabled={submitting}
                  onClick={() => onDevLogin(p.role)}
                  className={
                    "group flex items-center gap-3 rounded-md border " +
                    "border-base-200 bg-surface p-2 text-left transition-colors " +
                    "hover:border-ai-300 hover:bg-ai-50 " +
                    "disabled:cursor-not-allowed disabled:opacity-60"
                  }
                >
                  <img
                    src={`https://i.pravatar.cc/96?u=${p.avatarSeed}`}
                    alt=""
                    width={40}
                    height={40}
                    loading="lazy"
                    onError={(e) => {
                      // Hide the broken-image placeholder when
                      // pravatar is unreachable; the role label
                      // alone still works.
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                    className="size-10 shrink-0 rounded-full bg-base-100 object-cover ring-1 ring-base-200"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-sm text-base-900">
                      {t(p.nameKey)}
                    </div>
                    <div className="truncate text-2xs text-base-500">
                      {t(p.labelKey)}
                    </div>
                    <div className="mt-0.5 text-2xs font-mono tabular-nums text-ai-700">
                      {t("login.dev.role.scopes", { count: p.scopeCount })}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Keep the original full-width "dev-admin" button as
                a quick shortcut for muscle memory + Playwright
                smoke (Sprint CZ asserts on its presence). */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              leftIcon={<Sparkles className="size-4" />}
              onClick={() => onDevLogin("admin")}
              disabled={submitting}
              className="mt-3 w-full"
            >
              {t("login.dev.submit")}
            </Button>
          </div>
        </Card>

        <p className="mt-4 text-center text-xs text-base-500">
          {t("login.footer")}
        </p>
      </div>
    </div>
  );
}
